import { CSSProperties, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { FlexContainer, Pagination, Search } from 'components/common';
import { Box } from 'components/common/box';
import { DropdownSelectable } from 'components/common/dropdown';
import { Filters, FiltersActive } from 'components/common/filter';

const FILTER_ID = 'my-team-follow';

type UsersType = 'all' | 'participants-only';

interface Props {
  customFilters?: Parameters<typeof Filters>[0]['customFilters'];
  children: ReactNode;
  numberOfPages: number;
  style?: CSSProperties;
  title: string;
}

const MyTeamFollowBox = ({
  customFilters,
  children,
  numberOfPages,
  style,
  title
}: Props) => {
  const { messages } = useReduxState();

  const router = useRouter();

  const handlePageChange = ({ page }) => {
    const queryParams = { ...router.query };
    queryParams.page = page.toString();

    router.replace({ query: queryParams }, undefined, { shallow: true });
  };

  const handleUserTypeChange = (value: UsersType) => {
    const queryParams = { ...router.query };
    // Page is intentionally not reset here: in the product list view
    // participantsOnly only changes the stats shown per row, not the row count.
    // The users detail view, where it does shrink the list, clamps the page
    // locally instead.
    if (value === 'all') delete queryParams.participantsOnly;
    else queryParams.participantsOnly = 'true';

    router.replace({ query: queryParams }, undefined, { shallow: true });
  };

  return (
    <>
      <Box
        subHeaderContent={
          <FlexContainer
            style={{
              alignItems: 'flex-start',
              flexDirection: 'column',
              width: '100%'
            }}
          >
            <FlexContainer
              style={{
                flexWrap: 'wrap',
                gap: 10,
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <FlexContainer style={{ gap: 10 }}>
                <div style={{ width: 240 }}>
                  <Search withUrl={true} />
                </div>

                {customFilters?.length ? (
                  <Filters
                    customFilters={customFilters}
                    filterId={FILTER_ID}
                    filters={{}}
                  />
                ) : null}
              </FlexContainer>

              <DropdownSelectable
                category="lighter"
                defaultValue="all"
                onChange={handleUserTypeChange}
                options={[
                  { text: messages.myTeamShowMyTeam, value: 'all' },
                  {
                    text: messages.myTeamShowParticipantsOnly,
                    value: 'participants-only'
                  }
                ]}
                showSelectedText={true}
                value={
                  router.query.participantsOnly ? 'participants-only' : 'all'
                }
              />
            </FlexContainer>

            {customFilters?.length ? (
              <FiltersActive filterId={FILTER_ID} />
            ) : null}
          </FlexContainer>
        }
        padding={0}
        style={style}
        title={title}
      >
        {children}
      </Box>

      {numberOfPages ? (
        <Pagination
          currentPage={router.query.page ? Number(router.query.page) : 1}
          numberOfPages={numberOfPages}
          onPageChange={handlePageChange}
        />
      ) : null}
    </>
  );
};

export default MyTeamFollowBox;
